import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FolderIcon from '@mui/icons-material/Folder';
import ShareIcon from '@mui/icons-material/Share';

import { SLIDE_PREVIEW_SIZE } from '@/constant';
import { LessonFolder } from '@/models/lessonFolder';
import { useSession } from '@/providers/session/SessionProvider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, CardContent, Checkbox, Chip, Divider, IconButton, Link, Menu, MenuItem, Stack, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnHoverRef } from 'rooks';
import { isPointerPosition, PointerPosition } from '../../type';

const MENU_SPACING = 2;

export type Action = 'start' | 'rename' | 'remove' | 'share' | 'unshare';

export type FolderCardActionProp = {
    onSelected?: (folder: LessonFolder) => void;
    onPreviewClick?: (folder: LessonFolder) => void;
    onActionClick?: (folder: LessonFolder, action: Action) => void;
};

type FolderCardProp = {
    isSelecting?: boolean;
    isSelected?: boolean;
    folder: LessonFolder;
};

export function FolderCard({ folder, onActionClick, onPreviewClick, isSelecting, isSelected, onSelected }: FolderCardProp & FolderCardActionProp) {
    const { session } = useSession();
    const { t } = useTranslation();

    // ui state
    const [ctxtMenu, setCtxtMenu] = React.useState<HTMLElement | PointerPosition | null>(null);
    const [showCheckbox, setShowCheckbox] = React.useState(false);
    const [previewColor, setPreviewColor] = React.useState('inherit');

    const onMenuSelected = (action: Action) => {
        if (onActionClick) {
            onActionClick(folder, action);
        }
        setCtxtMenu(null);
    };

    const containerRef = useOnHoverRef(
        () => {
            setShowCheckbox(true);
        },
        () => {
            setShowCheckbox(false);
        },
    );

    const previewRef = useOnHoverRef(
        () => {
            setPreviewColor('text.secondary');
            setShowCheckbox(false);
        },
        () => {
            setPreviewColor('inherit');
            setShowCheckbox(true);
        },
    );

    const actionRef = useOnHoverRef(
        () => {
            setShowCheckbox(false);
        },
        () => {
            setShowCheckbox(true);
        },
    );

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onPreviewClick?.(folder);
        },
        [folder, onPreviewClick],
    );

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();

        setCtxtMenu(
            ctxtMenu === null
                ? {
                      mouseX: event.clientX + 2,
                      mouseY: event.clientY - 6,
                  }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                  // Other native context menus might behave different.
                  // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                  null,
        );

        // Prevent text selection lost after opening the context menu on Safari and Firefox
        const selection = document.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            setTimeout(() => {
                selection.addRange(range);
            });
        }
    };

    return (
        <Card
            sx={{
                padding: 0,
                backgroundColor: (theme) => theme.palette.grey[50],
                position: 'relative',
            }}
            onContextMenu={handleContextMenu}
            ref={containerRef}
        >
            <CardContent
                onClick={() => {
                    onSelected?.(folder);
                }}
                sx={{
                    width: SLIDE_PREVIEW_SIZE.width,
                }}
            >
                <Stack direction='row' spacing={1} sx={{ p: 1, pl: 2 }}>
                    <Stack direction='row' spacing={1} sx={{ width: '100%', flex: 1, alignItems: 'center' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1,
                                flex: 1,
                                minWidth: 0,
                                '&:hover': { cursor: 'pointer' },
                            }}
                            onClick={handleClick}
                            ref={previewRef}
                        >
                            <FolderIcon sx={{ color: previewColor }} />
                            <Link noWrap variant='h6' sx={{ color: previewColor }}>
                                {folder.name}
                            </Link>
                        </Box>
                        <IconButton
                            aria-label='actions'
                            onClick={(e) => {
                                e.stopPropagation();
                                setCtxtMenu(e.currentTarget);
                            }}
                            ref={actionRef}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Stack>
                </Stack>
                <Stack spacing={1} sx={{ px: 2, pb: 2, flex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', minHeight: 20 }}>
                        {session?.user?.teacherProfile && folder.isOwnedBy(session.user.teacherProfile) ? (
                            <Stack direction='row' spacing={1} sx={{ minHeight: 20 }}>
                                {folder.isShared() && <Chip label={t('lesson.folder.tag.shared')} color='primary' />}
                            </Stack>
                        ) : (
                            <Stack direction='row' spacing={1} sx={{ minHeight: 20 }}>
                                <Chip label={t('lesson.tag.readOnly')} color='secondary' />
                            </Stack>
                        )}
                        {(isSelecting || showCheckbox) && (
                            <Checkbox
                                checked={isSelected}
                                sx={{
                                    margin: 0,
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelected?.(folder);
                                }}
                            />
                        )}
                    </Box>
                </Stack>
            </CardContent>

            <Menu
                open={Boolean(ctxtMenu)}
                anchorEl={ctxtMenu instanceof HTMLElement ? ctxtMenu : undefined}
                anchorPosition={isPointerPosition(ctxtMenu) ? { top: ctxtMenu.mouseY, left: ctxtMenu.mouseX } : undefined}
                anchorReference={isPointerPosition(ctxtMenu) ? 'anchorPosition' : 'anchorEl'}
                onClose={() => {
                    setCtxtMenu(null);
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 12, // 8 for the button padding, 4 for the popver built-in top margin
                    horizontal: 'left',
                }}
            >
                {session?.user?.teacherProfile && (
                    <Box>
                        <Tooltip title={!folder.isOwnedBy(session.user.teacherProfile) ? t('lesson.folder.cannotRename') : ''}>
                            <span>
                                <MenuItem
                                    disabled={!folder.isOwnedBy(session.user.teacherProfile)}
                                    onClick={() => {
                                        onMenuSelected('rename');
                                    }}
                                >
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <DriveFileRenameOutlineIcon />
                                        <Typography>{t('lesson.folder.actions.rename')}</Typography>
                                    </Stack>
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <Tooltip title={!folder.isOwnedBy(session.user.teacherProfile) ? t('lesson.folder.cannotShare') : ''}>
                            <span>
                                <MenuItem
                                    disabled={!folder.isOwnedBy(session.user.teacherProfile)}
                                    onClick={() => {
                                        onMenuSelected('share');
                                    }}
                                >
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <ShareIcon />
                                        <Typography>{t('lesson.folder.actions.share')}</Typography>
                                    </Stack>
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <Divider sx={{ my: 1 }} />
                        {folder.isOwnedBy(session.user.teacherProfile) && (
                            <Tooltip title={!folder.isOwnedBy(session.user.teacherProfile) ? t('lesson.folder.cannotRemove') : ''}>
                                <span>
                                    <MenuItem
                                        onClick={() => {
                                            onMenuSelected('remove');
                                        }}
                                    >
                                        <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                            <DeleteIcon />
                                            <Typography>{t('lesson.folder.actions.remove')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        )}
                        {folder.isSharedWithMe(session.user.teacherProfile) && (
                            <Tooltip title={!folder.isSharedWithMe(session.user.teacherProfile) ? t('lesson.folder.cannotRemove') : ''}>
                                <span>
                                    <MenuItem
                                        onClick={() => {
                                            onMenuSelected('unshare');
                                        }}
                                    >
                                        <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                            <DeleteIcon />
                                            <Typography>{t('lesson.folder.actions.remove')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </Menu>
        </Card>
    );
}
